import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { outcomeLabel } from "@/lib/labels";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Phone, Calendar, ArrowUpRight, Bot, Clock, Sparkles, Check, Circle, PhoneCall, MessageSquare, Scissors, Users, BookOpen, Activity } from "lucide-react";

const steps = [
  { key: "business", label: "Business info", to: "/app/business" },
  { key: "services", label: "Services", to: "/app/services" },
  { key: "hours", label: "Business hours", to: "/app/hours" },
  { key: "ai", label: "AI receptionist", to: "/app/ai" },
  { key: "test", label: "Test call", to: "/app/test" },
  { key: "live", label: "Go live", to: "/app/settings" },
];

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

const initials = (name = "") =>
  name.split(" ").filter(Boolean).map(p => p[0]).slice(0, 2).join("").toUpperCase() || "•";

function Stat({ label, value, icon: Icon, chip, tid }) {
  return (
    <div data-testid={tid} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${chip}`}><Icon className="w-5 h-5" /></div>
        <div className="text-sm text-slate-500">{label}</div>
      </div>
      <div className="mt-3 text-3xl font-bold font-heading tracking-tight text-slate-950">{value}</div>
    </div>
  );
}

const OUTCOME_DOT = {
  appointment_booked: "bg-emerald-500", appointment_cancelled: "bg-red-500",
  appointment_rescheduled: "bg-amber-500", human_transfer: "bg-red-400",
  failed: "bg-red-500", info_request: "bg-blue-400",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState(null);
  const [usage, setUsage] = useState(null);
  const [biz, setBiz] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [quoteAmounts, setQuoteAmounts] = useState({});
  useEffect(() => {
    api.get("/dashboard/stats").then(r => setStats(r.data));
    api.get("/analytics/ai-performance").then(r => setPerf(r.data)).catch((err) => console.warn("ai-performance load failed:", err));
    api.get("/billing/usage").then(r => setUsage(r.data)).catch((err) => console.warn("usage load failed:", err));
    api.get("/business").then(r => setBiz(r.data)).catch((err) => console.warn("business load failed:", err));
    api.get("/analytics").then(r => setAnalytics(r.data)).catch((err) => console.warn("analytics load failed:", err));
    api.get("/quotes").then(r => setQuotes(r.data)).catch((err) => console.warn("quotes load failed:", err));
  }, []);
  const INDUSTRY_LABELS = { barbershop: "Barbershop", salon_spa: "Salon / Spa", dental: "Dental", medical: "Medical", law: "Law", home_services: "Home Services", real_estate: "Real Estate", restaurant: "Restaurant", auto_repair: "Auto Repair", car_detailing: "Car Detailing", property_management: "Property Mgmt", custom: "Custom" };
  if (!stats) return <div className="p-8 text-slate-500">Loading…</div>;
  const onboarding = stats.onboarding || {};
  const incomplete = steps.filter(s => !onboarding[s.key]);
  const firstName = (user?.name || "").trim().split(" ")[0];
  const tz = stats.timezone || undefined;
  const week = (analytics?.daily || []).slice(-7);
  const maxV = Math.max(1, ...week.map(d => Math.max(d.calls, d.appointments)));
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { timeZone: tz, hour: "numeric", minute: "2-digit" });
  const connected = !!biz?.vapi_assistant_id;

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-950" data-testid="dashboard-greeting">
            {greeting()}{firstName ? `, ${firstName}` : ""} <span aria-hidden>👋</span>
          </h1>
          <p className="text-slate-600 mt-1">Your AI receptionist is handling calls and booking appointments.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {biz?.demo && (
            <div className="text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200" data-testid="demo-industry-chip">
              Demo · <b>{INDUSTRY_LABELS[biz.industry] || biz.industry || "Custom"}</b>
            </div>
          )}
          {stats.subscription_status === "active" ? (
            <div className="text-sm text-slate-600 flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-full border border-emerald-100">
              <Check className="w-4 h-4 text-emerald-600" />
              <span data-testid="active-plan-badge"><b className="capitalize">{stats.plan || "Paid"}</b> plan · active</span>
            </div>
          ) : stats.subscription_status === "trial" ? (
            <div className="text-sm text-slate-600 flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <span data-testid="trial-days-badge"><b>{stats.trial_days_left}</b> days left in free trial</span>
            </div>
          ) : (
            <a href="/app/settings" className="text-sm flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-full border border-amber-200 text-amber-800 hover:bg-amber-100" data-testid="subscribe-badge">
              <Sparkles className="w-4 h-4" />
              <span>No active plan — <b>subscribe to activate</b></span>
            </a>
          )}
          <div className="text-sm text-slate-500" data-testid="dashboard-date">
            {new Date().toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>
      </div>

      {usage?.paused && (
        <div className="mt-4 p-4 rounded-2xl bg-red-50 border border-red-200 text-sm text-red-800 flex items-center justify-between gap-3 flex-wrap" data-testid="billing-banner-paused">
          <span><b>Call answering is paused.</b> {usage.banner?.text || "Your overage policy stopped answering."}</span>
          <Link to="/app/settings"><Button size="sm" variant="outline" className="rounded-xl" data-testid="billing-banner-manage">Manage billing</Button></Link>
        </div>
      )}
      {!usage?.paused && usage?.banner && (
        <div className="mt-4 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center justify-between gap-3 flex-wrap" data-testid="billing-banner-usage">
          <span>{usage.banner.text}</span>
          <Link to="/app/settings"><Button size="sm" variant="outline" className="rounded-xl" data-testid="billing-banner-view">View usage</Button></Link>
        </div>
      )}

      {incomplete.length > 0 && !stats.demo && (
        <div className="mt-6 bg-slate-950 text-white rounded-2xl p-6" data-testid="setup-banner">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-heading font-semibold text-lg">Finish setting up ASFA</div>
              <div className="text-sm text-slate-300 mt-0.5">{incomplete.length} step{incomplete.length > 1 ? "s" : ""} remaining before you go live.</div>
            </div>
            <Link to={incomplete[0].to}><Button className="rounded-xl bg-blue-600 hover:bg-blue-700">Continue setup <ArrowUpRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {steps.map(s => (
              <Link key={s.key} to={s.to} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-slate-900" data-testid={`setup-${s.key}`}>
                {onboarding[s.key] ? <Check className="w-4 h-4 text-emerald-400" /> : <Circle className="w-4 h-4 text-slate-500" />}
                <span className={onboarding[s.key] ? "text-slate-400 line-through" : "text-slate-200"}>{s.label}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Calls Handled" value={stats.calls_answered} icon={Phone} chip="bg-emerald-50 text-emerald-600" tid="stat-calls" />
        <Stat label="Appointments Booked" value={stats.appointments_booked} icon={Calendar} chip="bg-blue-50 text-blue-600" tid="stat-appts" />
        <Stat label="Human Transfers" value={stats.human_transfers} icon={Bot} chip="bg-violet-50 text-violet-600" tid="stat-transfers" />
        <Stat label="Est. Hours Saved" value={stats.hours_saved + "h"} icon={Clock} chip="bg-amber-50 text-amber-600" tid="stat-hours" />
      </div>

      {quotes.filter(q => q.status === "pending").length > 0 && (
        <div className="mt-4 bg-white border border-amber-200 rounded-2xl p-5" data-testid="quotes-card">
          <div className="font-heading font-semibold mb-1">Quote requests</div>
          <div className="text-xs text-slate-500 mb-3">Callers your receptionist couldn't price from your rules. Approve a quote and we text it to them instantly — they reply YES to book.</div>
          {quotes.filter(q => q.status === "pending").slice(0, 4).map(q => (
            <div key={q.id} className="flex items-center gap-2 py-2 border-t border-slate-100 flex-wrap" data-testid={`quote-row-${q.id}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-medium truncate">{q.customer_name || q.customer_phone} · {q.service || "Service"}</div>
                <div className="text-xs text-slate-500 truncate">{q.details}</div>
              </div>
              <Input data-testid={`quote-amount-${q.id}`} placeholder="$ amount" className="w-28 h-8 text-sm"
                value={quoteAmounts[q.id] || ""} onChange={(e) => setQuoteAmounts({ ...quoteAmounts, [q.id]: e.target.value })} />
              <Button data-testid={`quote-approve-${q.id}`} size="sm" className="rounded-xl" onClick={async () => {
                try {
                  await api.post(`/quotes/${q.id}/approve`, { amount: quoteAmounts[q.id] || "" });
                  toast.success("Quote texted to the customer");
                  setQuotes((await api.get("/quotes")).data);
                } catch (e) { toast.error(errMsg(e, "Couldn't send quote")); }
              }}>Approve & text</Button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold">Recent Calls</div>
            <Link to="/app/calls" className="text-sm text-blue-600 hover:underline">View all →</Link>
          </div>
          {stats.recent_calls.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No calls yet. Once ASFA answers, they'll appear here.</div>
          ) : (
            <div className="space-y-1.5" data-testid="recent-calls-list">
              {stats.recent_calls.slice(0, 5).map(c => (
                <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className={`w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0`}>
                    <span className={`w-2 h-2 rounded-full ${OUTCOME_DOT[c.outcome] || "bg-blue-400"}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{c.caller_name || c.caller_phone}</div>
                    <div className="text-xs text-slate-500 truncate">{outcomeLabel(c.outcome)}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-slate-600">{c.start_at ? fmtTime(c.start_at) : ""}</div>
                    {c.duration_sec > 0 && <div className="text-[11px] text-slate-400">{Math.max(1, Math.round(c.duration_sec / 60))} min</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold">Upcoming Appointments</div>
            <Link to="/app/appointments" className="text-sm text-blue-600 hover:underline">View calendar →</Link>
          </div>
          {stats.upcoming_appointments.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">No upcoming appointments yet.</div>
          ) : (
            <div className="space-y-1.5" data-testid="upcoming-list">
              {stats.upcoming_appointments.slice(0, 5).map(a => (
                <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="w-14 flex-shrink-0 text-sm font-semibold text-slate-900">{fmtTime(a.start)}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{a.service_name || "Appointment"}</div>
                    <div className="text-xs text-slate-500 truncate">{[a.staff_name && `with ${a.staff_name}`, a.created_by === "ai" ? "Booked by AI" : "Booked by staff"].filter(Boolean).join(" · ")}</div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-[11px] font-semibold text-slate-600 flex-shrink-0" title={a.customer_name}>
                    {initials(a.customer_name)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="test-receptionist-card">
          <div className="font-heading font-semibold">Test Your AI Receptionist</div>
          <p className="text-sm text-slate-500 mt-1">Try a call to hear how your AI sounds to customers.</p>
          <div className="mt-5 space-y-2.5">
            <Link to="/app/test" className="block">
              <Button data-testid="dash-test-call-btn" className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 h-11">
                <PhoneCall className="w-4 h-4 mr-2" />Start Test Call
              </Button>
            </Link>
            <Link to="/app/test" className="block">
              <Button data-testid="dash-test-text-btn" variant="outline" className="w-full rounded-xl h-11">
                <MessageSquare className="w-4 h-4 mr-2" />Test by Text Message
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-400">Private test — won't touch your live number or real customers.</p>
        </div>
      </div>

      <div className="mt-4 grid lg:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ai-status-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold">AI Receptionist Status</div>
            <span className={`flex items-center gap-1.5 text-xs font-medium ${connected ? "text-emerald-600" : "text-amber-600"}`} data-testid="ai-status-dot">
              <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-500"} animate-pulse`} />
              {connected ? "Active" : "Setup needed"}
            </span>
          </div>
          <div className="space-y-2.5 text-sm">
            {[["Answering calls", "24/7"], ["Booking appointments", "Enabled"], ["Sending confirmation SMS", "Enabled"], ["Collecting customer info", "Enabled"], ["Handling FAQs", "Enabled"]].map(([l, v]) => (
              <div key={l} className="flex items-center justify-between">
                <span className="text-slate-600">{l}</span>
                <span className="text-xs font-medium text-slate-500">{v}</span>
              </div>
            ))}
          </div>
          {usage && !usage.error && (
            <div className="mt-4 pt-4 border-t border-slate-100" data-testid="stat-minutes">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Minutes Used</span>
                <span className="font-semibold">{usage.used_minutes} / {usage.included_minutes}</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full rounded-full ${usage.percent_used >= 90 ? "bg-red-500" : usage.percent_used >= 75 ? "bg-amber-400" : "bg-emerald-500"}`}
                     style={{ width: `${Math.min(100, usage.percent_used)}%` }} />
              </div>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="week-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-semibold">This Week's Overview</div>
            <span className="text-xs text-slate-400">Calls vs appointments</span>
          </div>
          {week.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Activity will chart here as calls come in.</div>
          ) : (
            <>
              <div className="flex items-end gap-2 h-32">
                {week.map(d => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
                    <div className="flex items-end gap-1 h-24 w-full justify-center">
                      <div className="w-3 rounded-t bg-emerald-300 transition-all" style={{ height: `${Math.max(4, (d.calls / maxV) * 100)}%` }} title={`${d.calls} calls`} />
                      <div className="w-3 rounded-t bg-emerald-600 transition-all" style={{ height: `${Math.max(4, (d.appointments / maxV) * 100)}%` }} title={`${d.appointments} appointments`} />
                    </div>
                    <div className="text-[10px] text-slate-400">{new Date(d.date + "T12:00:00").toLocaleDateString([], { weekday: "short" })}</div>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-4 text-[11px] text-slate-500">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-300" />Calls</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-600" />Appointments</span>
              </div>
            </>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="quick-actions">
          <div className="font-heading font-semibold mb-3">Quick Actions</div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Scissors, label: "Manage Services", sub: "Add or edit your services", to: "/app/services", tid: "qa-services", chip: "bg-violet-50 text-violet-600" },
              { icon: Users, label: "Manage Team", sub: "Add or edit team members", to: "/app/team", tid: "qa-team", chip: "bg-blue-50 text-blue-600" },
              { icon: BookOpen, label: "AI Instructions", sub: "Customize how your AI handles calls", to: "/app/ai", tid: "qa-ai", chip: "bg-amber-50 text-amber-600" },
              { icon: Clock, label: "Business Hours", sub: "Set your availability", to: "/app/hours", tid: "qa-hours", chip: "bg-emerald-50 text-emerald-600" },
            ].map(q => (
              <Link key={q.tid} to={q.to} data-testid={q.tid}
                className="rounded-xl border border-slate-200 p-3.5 hover:border-blue-300 hover:shadow-sm transition-all group">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${q.chip}`}><q.icon className="w-4 h-4" /></div>
                <div className="mt-2.5 text-sm font-medium group-hover:text-blue-700">{q.label}</div>
                <div className="text-xs text-slate-500 mt-0.5 leading-snug">{q.sub}</div>
              </Link>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-sm">
            <span className="text-slate-500">Est. appointment value (this month)</span>
            <span className="font-bold font-heading text-lg" data-testid="stat-value">${stats.estimated_value.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {perf && (
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-6" data-testid="ai-performance-card">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="font-heading font-semibold text-lg flex items-center gap-2"><Activity className="w-4 h-4 text-blue-600" />AI performance</div>
            {perf.knowledge_gaps_open > 0 && (
              <Link to="/app/knowledge" className="text-sm text-amber-600 hover:underline" data-testid="gaps-link">
                {perf.knowledge_gaps_open} unanswered question{perf.knowledge_gaps_open > 1 ? "s" : ""} →
              </Link>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4 text-sm">
            <div><div className="text-xs text-slate-500">Calls handled</div><div className="text-xl font-bold font-heading mt-1">{perf.calls_handled}</div></div>
            <div><div className="text-xs text-slate-500">AI bookings</div><div className="text-xl font-bold font-heading mt-1">{perf.ai_bookings}</div></div>
            <div><div className="text-xs text-slate-500">Avg response</div><div className="text-xl font-bold font-heading mt-1">{(perf.avg_latency_ms / 1000).toFixed(1)}s</div></div>
            <div><div className="text-xs text-slate-500">p95 response</div><div className="text-xl font-bold font-heading mt-1">{(perf.p95_latency_ms / 1000).toFixed(1)}s</div></div>
            <div><div className="text-xs text-slate-500">Transfers</div><div className="text-xl font-bold font-heading mt-1">{perf.transfers}</div></div>
            <div><div className="text-xs text-slate-500">Failed actions</div><div className="text-xl font-bold font-heading mt-1">{perf.failed_tool_calls}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}
